import { useMemo, useRef, useState } from "react";

import { AlertTriangle, CheckCircle2, CloudDownload, FileUp, RefreshCw, Tag } from "lucide-react";

import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { StatusBadge } from "@/components/cinch/StatusBadge/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCan } from "@/lib/auth";

import {
  useIngestFromSharePoint,
  useIngestRuns,
  useIngestStatus,
  useReapplyRules,
  useUnmapped,
  useUploadBankFiles,
  type IngestRun,
  type UnmappedGroup,
} from "../api";
import { fmtUsd, weekLabel } from "../lib";
import { ClassifyVendorDialog } from "./ClassifyVendorDialog";

/** Tie-out is the number that matters: the bank statement total for the week
 *  minus what the engine computes, i.e. the workbook's Forecast row 38. Zero
 *  means we reproduce the bank; anything else needs explaining before the week
 *  can be locked. Null means there's no modelled week to compare against.
 *
 *  Same direction and same source as the lock gate, so a week that reads
 *  "Ties" here is a week the Lock button will accept. It used to be measured
 *  against the published run's stored forecast, which made clean weeks show
 *  five-figure variances. */
function TieOut({ run }: { run: IngestRun }) {
  if (run.tie_out_variance === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  const variance = run.tie_out_variance;
  const ties = Math.abs(variance) < 0.01;
  // Cents, not whole dollars: a sub-dollar variance is still a variance, and
  // rounding it to "-$0" makes a real difference look like a rendering bug.
  const amount = variance.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
  return (
    <span
      className={`inline-flex items-center gap-1 ${ties ? "text-success" : "text-danger"}`}
      title={ties ? "Bank statement total matches the model" : "Bank statement total differs from the model"}
    >
      {ties ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
      {ties ? "Ties" : amount}
    </span>
  );
}

function RunFiles({ run }: { run: IngestRun }) {
  if (run.files.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {run.files.map((f) => (
        <Badge
          key={f.id}
          variant={
            f.status === "parsed" ? "success" : f.status === "failed" ? "danger" : "warning"
          }
          title={f.error ?? `${f.filename} · ${f.row_count} rows`}
        >
          {f.account_code ?? f.filename}
          {f.status === "parsed" ? ` · ${f.row_count}` : ` · ${f.status}`}
        </Badge>
      ))}
    </div>
  );
}

function SourceCard() {
  const { data: status, isLoading, refetch, isRefetching } = useIngestStatus();
  const pull = useIngestFromSharePoint();
  const canWrite = useCan("cash_flow.write");
  const [week, setWeek] = useState<string>("");

  const newWeeks = useMemo(() => {
    if (!status) return [];
    const done = new Set(status.ingested_weeks);
    return status.available_weeks.filter((w) => !done.has(w));
  }, [status]);
  // available_weeks arrives newest-first.
  const newestPending = newWeeks[0];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>SharePoint — FP&amp;A bank exports</CardTitle>
            <CardDescription>
              Read straight from{" "}
              <code className="rounded bg-muted px-1">{status?.folder ?? "Bank Exports"}</code> in
              the FP&amp;A site library. Nothing changes for whoever saves the files — drop the
              week's seven exports in as usual and pull them in here.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isRefetching}
          >
            <RefreshCw className={`h-4 w-4 ${isRefetching ? "animate-spin" : ""}`} />
            Check for new weeks
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Checking SharePoint…</p>
        ) : status?.configured === false ? (
          <div className="rounded-md border border-warning/40 bg-warning-light p-3 text-sm">
            <p className="font-medium">SharePoint access isn't configured yet.</p>
            <p className="mt-1 text-muted-foreground">
              Needs an Azure AD app registration with a <b>Sites.Selected</b> grant on the FP&amp;A
              site, then <code className="rounded bg-muted px-1">CINCH_OPS_GRAPH_*</code> set on the
              server. Until then, use manual upload below — it runs the same pipeline.
            </p>
          </div>
        ) : status?.error ? (
          <div className="rounded-md border border-danger/40 bg-danger-light p-3 text-sm text-danger">
            {status.error}
          </div>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sp-week">Week folder</Label>
              <Select value={week} onValueChange={setWeek}>
                <SelectTrigger id="sp-week" className="w-52">
                  <SelectValue placeholder="Newest not yet ingested" />
                </SelectTrigger>
                <SelectContent>
                  {status?.available_weeks.map((w) => (
                    <SelectItem key={w} value={w}>
                      {weekLabel(w, true)}
                      {status.ingested_weeks.includes(w) ? " · ingested" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              disabled={!canWrite || pull.isPending}
              onClick={() => pull.mutate(week ? { week_folder: week } : {})}
            >
              <CloudDownload className="h-4 w-4" />
              {pull.isPending ? "Pulling…" : "Pull from SharePoint"}
            </Button>
            {newestPending && (
              <p className="text-sm text-muted-foreground">
                {newWeeks.length} week{newWeeks.length === 1 ? "" : "s"} not yet ingested — newest{" "}
                <b className="text-foreground">{weekLabel(newestPending, true)}</b>
              </p>
            )}
          </div>
        )}
        {pull.isError && (
          <p className="text-sm text-danger">Pull failed: {pull.error.message}</p>
        )}
      </CardContent>
    </Card>
  );
}

function UploadCard() {
  const upload = useUploadBankFiles();
  const canWrite = useCan("cash_flow.write");
  const [weekFolder, setWeekFolder] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = () => {
    if (!weekFolder || files.length === 0) return;
    upload.mutate(
      { weekFolder, files },
      {
        onSuccess: () => {
          setFiles([]);
          if (inputRef.current) inputRef.current.value = "";
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Manual upload</CardTitle>
        <CardDescription>
          The fallback path, for when SharePoint is unavailable or a single export needs
          replacing. Same pipeline, same tie-out. Files are matched to accounts by filename, so
          no renaming is needed.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="upload-week">Week ending</Label>
            <Input
              id="upload-week"
              type="date"
              value={weekFolder}
              onChange={(e) => setWeekFolder(e.target.value)}
              className="w-44"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="upload-files">Bank exports (CSV)</Label>
            <Input
              id="upload-files"
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
              className="w-96"
            />
          </div>
          <Button
            disabled={!canWrite || !weekFolder || files.length === 0 || upload.isPending}
            onClick={submit}
          >
            <FileUp className="h-4 w-4" />
            {upload.isPending ? "Ingesting…" : `Ingest ${files.length || ""} file${files.length === 1 ? "" : "s"}`}
          </Button>
        </div>
        {upload.isError && (
          <p className="text-sm text-danger">Upload failed: {upload.error.message}</p>
        )}
      </CardContent>
    </Card>
  );
}

function UnmappedCard() {
  const { data: groups, isLoading } = useUnmapped();
  const reapply = useReapplyRules();
  const canWrite = useCan("cash_flow.write");
  const [classifying, setClassifying] = useState<UnmappedGroup | null>(null);

  const columns = useMemo<DataTableColumn<UnmappedGroup>[]>(
    () => [
      {
        key: "description",
        header: "Description",
        render: (g) => (
          <span className="block max-w-md truncate font-mono text-xs" title={g.description}>
            {g.description}
          </span>
        ),
      },
      { key: "count", header: "Rows", numeric: true, align: "right" },
      {
        key: "total_amount",
        header: "Total",
        numeric: true,
        align: "right",
        render: (g) => (
          <span className={g.total_amount < 0 ? "text-danger" : undefined}>
            {fmtUsd(g.total_amount)}
          </span>
        ),
      },
      {
        key: "last_seen",
        header: "Last seen",
        render: (g) => weekLabel(g.last_seen, true),
      },
      {
        key: "sample_transaction_id",
        header: "",
        align: "right",
        render: (g) => (
          <Button
            variant="outline"
            size="sm"
            disabled={!canWrite}
            onClick={() => setClassifying(g)}
          >
            <Tag className="h-3.5 w-3.5" />
            Classify
          </Button>
        ),
      },
    ],
    [canWrite],
  );

  const total = groups?.reduce((sum, g) => sum + g.count, 0) ?? 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>
              Unclassified vendors
              {total > 0 && <Badge variant="warning" className="ml-2">{total} rows</Badge>}
            </CardTitle>
            <CardDescription>
              Transactions no mapping rule matched, grouped by vendor. This is the "what's this
              one?" list — add a rule on the Mapping tab and every past and future week
              reclassifies at once.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={!canWrite || reapply.isPending}
            onClick={() => reapply.mutate()}
          >
            <RefreshCw className={`h-4 w-4 ${reapply.isPending ? "animate-spin" : ""}`} />
            Re-apply rules
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {reapply.isSuccess && (
          <p className="text-sm text-muted-foreground">
            Scanned {reapply.data.scanned.toLocaleString()} transactions ·{" "}
            <b className="text-foreground">{reapply.data.rebucketed.toLocaleString()}</b>{" "}
            re-bucketed · {reapply.data.overrides_reapplied.toLocaleString()} manual overrides
            re-applied.
          </p>
        )}
        <DataTable
          columns={columns}
          rows={groups ?? []}
          rowKey={(g) => g.sample_row_hash}
          loading={isLoading}
          emptyMessage="Nothing unclassified — every transaction matched a rule."
        />
      </CardContent>
      <ClassifyVendorDialog group={classifying} onClose={() => setClassifying(null)} />
    </Card>
  );
}

function HistoryCard() {
  const { data: runs, isLoading } = useIngestRuns();

  const columns = useMemo<DataTableColumn<IngestRun>[]>(
    () => [
      {
        key: "week_folder",
        header: "Week",
        render: (r) => (
          <span className="inline-flex items-center gap-2">
            {weekLabel(r.week_folder, true)}
            <StatusBadge value={r.status} />
          </span>
        ),
      },
      {
        key: "source",
        header: "Source",
        render: (r) => (r.source === "graph" ? "SharePoint" : "Upload"),
      },
      { key: "files_matched", header: "Files", numeric: true, align: "right" },
      {
        key: "rows_ingested",
        header: "Rows",
        numeric: true,
        align: "right",
        render: (r) => r.rows_ingested.toLocaleString(),
      },
      {
        key: "statement_total",
        header: "Bank total",
        numeric: true,
        align: "right",
        render: (r) => (r.statement_total === null ? "—" : fmtUsd(r.statement_total)),
      },
      { key: "tie_out_variance", header: "Tie-out", render: (r) => <TieOut run={r} /> },
      {
        key: "accounts_missing",
        header: "Accounts / files",
        render: (r) => (
          <div className="flex flex-col gap-1">
            <RunFiles run={r} />
            {r.accounts_missing.length > 0 && (
              <span className="text-xs text-warning">
                Missing: {r.accounts_missing.join(", ")}
              </span>
            )}
            {(r.warnings ?? []).map((w) => (
              <span key={w} className="flex items-start gap-1 text-xs text-warning">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                {w}
              </span>
            ))}
            {r.error && <span className="text-xs text-danger">{r.error}</span>}
          </div>
        ),
      },
      {
        key: "created_at",
        header: "Ingested",
        render: (r) => new Date(r.created_at).toLocaleString(),
      },
    ],
    [],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ingest history</CardTitle>
        <CardDescription>
          Every week that's landed, with its bank-statement total and tie-out against the model.
          Re-ingesting a week replaces it rather than duplicating, so a corrected export is safe
          to re-run.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          rows={runs ?? []}
          rowKey={(r) => r.id}
          loading={isLoading}
          emptyMessage="No weeks ingested yet."
        />
      </CardContent>
    </Card>
  );
}

/** Bank-file intake: pull from SharePoint (preferred) or upload by hand,
 *  then review what landed and what nothing could classify. */
export function UploadsTab() {
  return (
    <div className="flex flex-col gap-6">
      <SourceCard />
      <UploadCard />
      <HistoryCard />
      <UnmappedCard />
    </div>
  );
}
